// ESPACE membre — Login / Register / Dashboard / Profile / Invitation accept
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMember, formatErr } from "@/contexts/MemberAuthContext";
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import PasswordInput from "@/components/forms/PasswordInput";
import NotificationPrefs from "@/components/NotificationPrefs";
import api from "@/lib/api";

const HERO = "/photos/prieure-meadow.jpg";

function Shell({ title, children, compact = false }) {
  // `compact` mode reduces vertical footprint so login forms stay above the fold
  const heroHeight = compact ? "18vh" : "32vh";
  const heroMinHeight = compact ? 140 : 240;
  const heroPadding = compact ? "pb-4" : "pb-8";
  const titleClass = compact ? "text-2xl sm:text-3xl" : "text-4xl sm:text-5xl";
  const bodyPadding = compact ? "py-6" : "py-16";
  return (
    <div data-testid="espace-page">
      <section className="relative" style={{ height: heroHeight, minHeight: heroMinHeight }}>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.55), rgba(20,16,12,0.4))" }} />
        <div className={`container-prieure relative h-full flex flex-col justify-end ${heroPadding}`}>
          <Link to="/" className="font-caps text-[0.65rem] text-white/80" style={{ letterSpacing: "0.25em" }}>← ESPACE MEMBRE</Link>
          <h1 className={`font-display text-white mt-2 ${titleClass}`}>{title}</h1>
        </div>
      </section>
      <div className={bodyPadding}>{children}</div>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label className="block">
      <span className="label-caps">{label}</span>
      <input {...rest} className="input-classic mt-1" />
    </label>
  );
}

// =========================================================== LOGIN
export function EspaceLogin() {
  const { member, login } = useMember();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  if (member) return <Navigate to={loc.state?.from || "/espace"} replace />;

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await login(form.email, form.password); nav("/espace"); }
    catch (ex) { setErr(formatErr(ex)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Connexion à mon espace" compact>
      <div className="container-prieure max-w-sm">
        <form onSubmit={submit} data-testid="espace-login-form" className="bg-white border p-6 space-y-3" style={{ borderColor: "var(--border)" }}>
          <Field label="EMAIL" type="email" required value={form.email} onChange={(e)=>setForm(s=>({...s,email:e.target.value}))} />
          <label className="block">
            <span className="label-caps">MOT DE PASSE</span>
            <div className="mt-1">
              <PasswordInput testId="espace-login-password" required value={form.password}
                             onChange={(e)=>setForm(s=>({...s,password:e.target.value}))}
                             autoComplete="current-password" />
            </div>
          </label>
          {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
          <button type="submit" disabled={busy} data-testid="espace-login-submit" className="btn btn-primary w-full">{busy ? "..." : "Se connecter"}</button>
          <div className="flex justify-between text-sm" style={{ color: "var(--ink-2)" }}>
            <Link to="/espace/mot-de-passe-oublie" data-testid="forgot-password-link" className="link-underline">
              Mot de passe oublié ?
            </Link>
            <Link to="/espace/inscription" className="link-underline">Créer un compte</Link>
          </div>
        </form>
      </div>
    </Shell>
  );
}

// =========================================================== REGISTER
export function EspaceRegister() {
  const { member, register } = useMember();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "participant" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  if (member) return <Navigate to="/espace" replace />;

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await register(form); nav("/espace"); }
    catch (ex) { setErr(formatErr(ex)); } finally { setBusy(false); }
  };
  const on = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));

  return (
    <Shell title="Créer mon espace" compact>
      <div className="container-prieure max-w-sm">
        <form onSubmit={submit} data-testid="espace-register-form" className="bg-white border p-6 space-y-3" style={{ borderColor: "var(--border)" }}>
          <Field label="NOM COMPLET" required value={form.name} onChange={on("name")} />
          <Field label="EMAIL" type="email" required value={form.email} onChange={on("email")} />
          <label className="block">
            <span className="label-caps">MOT DE PASSE (8 caractères minimum)</span>
            <div className="mt-1">
              <PasswordInput testId="espace-register-password" required minLength={8} value={form.password}
                             onChange={on("password")} autoComplete="new-password" />
            </div>
          </label>
          <div>
            <span className="label-caps">JE M'INSCRIS COMME</span>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { value: "participant", label: "Participant·e" },
                { value: "benevole", label: "Bénévole" },
              ].map(opt => (
                <button type="button" key={opt.value} onClick={() => setForm(s => ({ ...s, role: opt.value }))}
                        data-testid={`role-${opt.value}`}
                        className="px-4 py-3 border transition-colors"
                        style={{
                          background: form.role === opt.value ? "var(--cinnabar)" : "transparent",
                          color: form.role === opt.value ? "white" : "var(--ink)",
                          borderColor: form.role === opt.value ? "var(--cinnabar)" : "var(--border)",
                        }}>
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
              L'inscription en tant qu'organisateur se fait uniquement sur invitation par email.
            </p>
          </div>
          {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
          <button type="submit" disabled={busy} data-testid="espace-register-submit" className="btn btn-primary w-full">{busy ? "..." : "Créer mon espace"}</button>
          <p className="text-sm text-center" style={{ color: "var(--ink-2)" }}>
            Déjà inscrit ? <Link to="/espace/connexion" className="link-underline">Se connecter</Link>
          </p>
        </form>
      </div>
    </Shell>
  );
}

// =========================================================== INVITATION ACCEPT
export function EspaceInvitation() {
  const [params] = useSearchParams();
  const { member, espace } = useMember();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const token = params.get("token") || "";

  useEffect(() => {
    if (!token) { setErr("Lien d'invitation manquant."); return; }
    espace.get(`/espace/invitation/${token}`)
      .then(r => { setInvite(r.data); setForm(s => ({ ...s, name: r.data.name || "" })); })
      .catch(ex => setErr(formatErr(ex)));
  }, [token, espace]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await espace.post("/espace/invitation/accept", { token, ...form });
      window.location.href = "/espace";
    } catch (ex) { setErr(formatErr(ex)); } finally { setBusy(false); }
  };
  if (member) return <Navigate to="/espace" replace />;

  return (
    <Shell title="Activer mon compte organisateur" compact>
      <div className="container-prieure max-w-sm">
        {!invite && !err && <p className="text-center">Chargement…</p>}
        {err && <p className="text-center" style={{ color: "var(--cinnabar)" }}>{err}</p>}
        {invite && (
          <form onSubmit={submit} className="bg-white border p-6 space-y-3" style={{ borderColor: "var(--border)" }}>
            <p className="text-sm" style={{ color: "var(--ink-2)" }}>
              Vous avez été invité·e à rejoindre l'équipe en tant que <strong>{invite.role}</strong>.
            </p>
            <Field label="EMAIL" value={invite.email} disabled />
            <Field label="NOM COMPLET" required value={form.name} onChange={(e)=>setForm(s=>({...s,name:e.target.value}))} />
            <label className="block">
              <span className="label-caps">CHOISIR UN MOT DE PASSE</span>
              <div className="mt-1">
                <PasswordInput testId="invite-password" required minLength={8} value={form.password}
                               onChange={(e)=>setForm(s=>({...s,password:e.target.value}))}
                               autoComplete="new-password" />
              </div>
            </label>
            <button type="submit" disabled={busy} className="btn btn-primary w-full">{busy ? "..." : "Activer mon compte"}</button>
          </form>
        )}
      </div>
    </Shell>
  );
}

// =========================================================== DASHBOARD ROUTING
export function EspaceDashboard() {
  const { member } = useMember();
  if (member === null) return <Shell title="Mon espace"><p className="text-center">Chargement…</p></Shell>;
  if (!member) return <Navigate to="/espace/connexion" replace />;
  // Force password change if flagged by admin reset/set
  if (member.must_change_password) return <Navigate to="/espace/mot-de-passe?force=1" replace />;
  if (member.role === "organisateur") return <EspaceOrganisateur />;
  if (member.role === "benevole") return <EspaceBenevole />;
  return <EspaceParticipant />;
}

function DashHeader() {
  const { member, logout } = useMember();
  const { locale } = useI18n();
  const roleLabel = { participant: "Participant·e", benevole: "Bénévole", organisateur: "Organisateur·trice" }[member.role] || member.role;
  return (
    <section className="py-12" style={{ background: "var(--ink)", color: "white" }}>
      <div className="container-prieure flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>{roleLabel.toUpperCase()}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{member.name}</h1>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>{member.email}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/espace/profil" data-testid="espace-profile-link" className="btn btn-outline" style={{ color: "white", borderColor: "white" }}>
            {locale === "fr" ? "Mon profil" : "My profile"}
          </Link>
          <button onClick={logout} data-testid="espace-logout" className="btn btn-ghost" style={{ color: "white", borderColor: "transparent" }}>
            {locale === "fr" ? "Déconnexion" : "Logout"}
          </button>
        </div>
      </div>
    </section>
  );
}

function Card({ title, body, footer, tone = "white", testid }) {
  return (
    <article data-testid={testid} className="p-8 border h-full" style={{ borderColor: "var(--border)", background: tone === "white" ? "white" : "var(--parchment-2)" }}>
      <h3 className="font-display text-2xl mb-3">{title}</h3>
      <div className="font-body" style={{ color: "var(--ink-2)" }}>{body}</div>
      {footer && <div className="mt-6">{footer}</div>}
    </article>
  );
}

// ----- Personal iCal agenda card (subscribe URL + regenerate) ---------------
function AgendaCard() {
  const { espace } = useMember();
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    espace.get("/espace/agenda-link")
      .then(r => setLink(r.data))
      .catch(e => setErr(formatErr(e)));
  }, [espace]);

  const onCopy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback: select+copy via a hidden textarea
      const ta = document.createElement("textarea");
      ta.value = link.url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch (_e) { /* clipboard fallback failed */ }
      document.body.removeChild(ta);
    }
  };

  const onRegenerate = async () => {
    if (!window.confirm("Régénérer le lien rendra l'ancien immédiatement invalide. Vous devrez le recopier dans vos applications calendrier. Continuer ?")) return;
    setBusy(true);
    setErr("");
    try {
      const r = await espace.post("/espace/agenda-link/regenerate");
      setLink(r.data);
      setCopied(false);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid="card-agenda"
      className="p-8 border h-full md:col-span-2 lg:col-span-3"
      style={{ borderColor: "var(--gold)", background: "var(--parchment-2)" }}
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>AGENDA PERSONNEL</div>
          <h3 className="font-display text-2xl mt-1">📅 Mon agenda</h3>
          <p className="font-body text-sm mt-2 max-w-2xl" style={{ color: "var(--ink-2)" }}>
            Importez vos inscriptions Fêtes 2027, vos tickets de concerts et vos pré-inscriptions dans Google Calendar, Apple Calendar ou Outlook. Le calendrier reste à jour automatiquement.
          </p>
        </div>
      </div>

      {!link && !err && (
        <p className="font-body text-sm mt-6" style={{ color: "var(--muted)" }}>Chargement du lien…</p>
      )}
      {err && (
        <p className="font-body text-sm mt-6" style={{ color: "var(--cinnabar)" }} data-testid="agenda-error">{err}</p>
      )}
      {link && (
        <div className="mt-6 space-y-3">
          <div className="flex items-stretch gap-2 flex-wrap">
            <input
              type="text"
              readOnly
              value={link.url}
              onFocus={(e) => e.target.select()}
              data-testid="agenda-link-input"
              className="input-classic flex-1 font-mono text-xs"
              style={{ minWidth: 280 }}
            />
            <button
              onClick={onCopy}
              data-testid="agenda-link-copy"
              className="btn btn-primary text-sm"
            >
              {copied ? "✓ Copié" : "Copier le lien"}
            </button>
            <button
              onClick={onRegenerate}
              disabled={busy}
              data-testid="agenda-link-regenerate"
              className="btn btn-outline text-sm"
            >
              {busy ? "…" : "Régénérer"}
            </button>
          </div>
          <p className="font-body text-xs" style={{ color: "var(--muted)" }}>
            Lien valide {link.expires_in_days} jours. Régénérez-le si vous suspectez qu'il a été partagé.
          </p>
          <details className="text-xs mt-2" style={{ color: "var(--ink-2)" }}>
            <summary className="cursor-pointer font-caps" style={{ letterSpacing: "0.15em" }}>Comment l'utiliser ?</summary>
            <ul className="mt-2 space-y-1 list-disc pl-5">
              <li><strong>Google Calendar</strong> : Paramètres → Ajouter un agenda → À partir de l'URL → coller le lien.</li>
              <li><strong>Apple Calendar</strong> : Fichier → Nouvel abonnement à un calendrier → coller le lien.</li>
              <li><strong>Outlook</strong> : Ajouter un calendrier → Abonnement à partir du Web → coller le lien.</li>
            </ul>
          </details>
        </div>
      )}
    </article>
  );
}

// =========================================================== PARTICIPANT
function EspaceParticipant() {
  return (
    <div data-testid="espace-participant">
      <DashHeader />
      <div className="container-prieure py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AgendaCard />
        <Card testid="card-prereg" title="Ma pré-inscription"
          body={<p>Statut : <strong style={{ color: "var(--moss)" }}>Enregistrée</strong>. Les inscriptions définitives ouvriront prochainement.</p>}
          footer={<Link to="/presence/pre-inscription" className="link-underline text-sm">Modifier mes informations →</Link>} />
        <Card testid="card-livret" title="Livret des Fêtes"
          body={<p>Le programme complet, les horaires, les itinéraires et les informations pratiques.</p>}
          footer={<a href="/documents/livret-fetes-saint-leonard.pdf" target="_blank" rel="noopener noreferrer" className="link-underline text-sm">Télécharger le livret PDF →</a>} />
        <Card testid="card-badge" title="Mon QR badge"
          body={<p>Votre QR de check-in sera émis dès l'ouverture des inscriptions définitives.</p>}
          footer={<span className="text-xs" style={{ color: "var(--muted)" }}>À venir</span>} tone="parchment" />
        <Card testid="card-forum" title="Forum / Discussion"
          body={<p>Échangez avec les autres participants, partagez vos questions et préparatifs.</p>}
          footer={<Link to="/espace/forum" className="link-underline text-sm">Rejoindre les discussions →</Link>} />
        <Card testid="card-covoit" title="Covoiturage"
          body={<p>Proposez ou recherchez un trajet vers Vains avec d'autres participants.</p>}
          footer={<Link to="/espace/covoiturage" className="link-underline text-sm">Voir les trajets →</Link>} />
        <Card testid="card-photos" title="Galerie photos"
          body={<p>Revivez les éditions passées en images et partagez vos clichés.</p>}
          footer={<Link to="/espace/galerie" className="link-underline text-sm">Ouvrir la galerie →</Link>} />
        <Card testid="card-hebergements" title="Hébergements"
          body={<p>Liste recommandée des hôtels et maisons d'hôtes près du Prieuré.</p>}
          footer={<Link to="/espace/hebergements" className="link-underline text-sm">Voir les hébergements →</Link>} />
        <Card testid="card-password" title="Sécurité"
          body={<p>Changer mon mot de passe ou mettre à jour mes informations.</p>}
          footer={<Link to="/espace/mot-de-passe" className="link-underline text-sm">Changer mon mot de passe →</Link>} tone="parchment" />
      </div>
    </div>
  );
}

// =========================================================== BENEVOLE
function EspaceBenevole() {
  const { espace } = useMember();
  const [missions, setMissions] = useState([]);
  useEffect(() => {
    espace.get("/espace/missions").then(r => setMissions(r.data || [])).catch(() => setMissions([]));
  }, [espace]);
  return (
    <div data-testid="espace-benevole">
      <DashHeader />
      <div className="container-prieure py-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        <AgendaCard />
        <Card testid="card-missions" title={`Mes missions (${missions.length})`}
          body={missions.length === 0 ? (
            <p>Aucune mission assignée pour l'instant. Les coordinateurs vous contacteront prochainement.</p>
          ) : (
            <ul className="space-y-3">
              {missions.map(m => (
                <li key={m.id} className="border-l-2 pl-3" style={{ borderColor: "var(--gold)" }}>
                  <div className="font-display text-lg">{m.title}</div>
                  <div className="text-xs" style={{ color: "var(--muted)" }}>{m.starts_at} · {m.location}</div>
                  <p className="text-sm mt-1">{m.description}</p>
                </li>
              ))}
            </ul>
          )} />
        <Card testid="card-contact" title="Coordinateur bénévoles"
          body={<p>Pour toute question : <a href="mailto:benevoles@prieure-saint-leonard.fr" className="link-underline">benevoles@prieure-saint-leonard.fr</a></p>} />
        <Card testid="card-livret-b" title="Livret du bénévole"
          body={<p>Charte d'engagement, missions types, code vestimentaire et règles de sécurité.</p>}
          footer={<a href="/documents/livret-fetes-saint-leonard.pdf" target="_blank" rel="noopener noreferrer" className="link-underline text-sm">Télécharger →</a>} />
        <Card testid="card-disponibilites" title="Mes disponibilités"
          body={<p>Indiquez les créneaux pendant lesquels vous pouvez aider.</p>}
          footer={<Link to="/espace/disponibilites" className="link-underline text-sm">Gérer mes créneaux →</Link>} />
      </div>
    </div>
  );
}

// =========================================================== ORGANISATEUR
function EspaceOrganisateur() {
  const { espace } = useMember();
  const [members, setMembers] = useState([]);
  const [missions, setMissions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "" });
  const [inviteMsg, setInviteMsg] = useState("");

  const refresh = async () => {
    const [m, mi] = await Promise.all([
      espace.get("/espace/members").catch(() => ({ data: [] })),
      espace.get("/espace/missions").catch(() => ({ data: [] })),
    ]);
    setMembers(m.data); setMissions(mi.data);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const counts = members.reduce((a, m) => { a[m.role] = (a[m.role] || 0) + 1; return a; }, {});

  const sendInvite = async (e) => {
    e.preventDefault(); setBusy(true); setInviteMsg("");
    try {
      const r = await espace.post("/admin/espace/invitations", invite);
      setInviteMsg(`✓ Invitation envoyée à ${invite.email}.`);
      setInvite({ email: "", name: "" });
    } catch (ex) { setInviteMsg(formatErr(ex)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="espace-organisateur">
      <DashHeader />
      <div className="container-prieure py-16 space-y-10">
        {/* Personal agenda card */}
        <div className="grid grid-cols-1 gap-6"><AgendaCard /></div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Participants", count: counts.participant || 0, color: "var(--lapis)" },
            { label: "Bénévoles", count: counts.benevole || 0, color: "var(--moss)" },
            { label: "Organisateurs", count: counts.organisateur || 0, color: "var(--cinnabar)" },
            { label: "Missions", count: missions.length, color: "var(--gold)" },
          ].map((s, i) => (
            <div key={i} data-testid={`stat-${s.label.toLowerCase()}`} className="p-6 bg-white border" style={{ borderColor: "var(--border)", borderTopWidth: 3, borderTopColor: s.color }}>
              <div className="font-display text-5xl" style={{ color: s.color }}>{s.count}</div>
              <div className="font-caps text-[0.65rem] mt-2" style={{ letterSpacing: "0.2em", color: "var(--muted)" }}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>

        {/* Invite organizer */}
        <div className="bg-white border p-8" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-display text-2xl mb-4">Inviter un organisateur</h3>
          <form onSubmit={sendInvite} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input required type="email" placeholder="email@exemple.fr" value={invite.email} onChange={(e)=>setInvite(s=>({...s,email:e.target.value}))} className="input-classic" />
            <input placeholder="Nom (optionnel)" value={invite.name} onChange={(e)=>setInvite(s=>({...s,name:e.target.value}))} className="input-classic" />
            <button type="submit" disabled={busy} className="btn btn-primary">{busy ? "..." : "Envoyer l'invitation"}</button>
          </form>
          {inviteMsg && <p className="text-sm mt-3" style={{ color: inviteMsg.startsWith("✓") ? "var(--moss)" : "var(--cinnabar)" }}>{inviteMsg}</p>}
        </div>

        {/* Members list */}
        <div className="bg-white border p-8" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="font-display text-2xl">Annuaires</h3>
            <div className="flex gap-2">
              <Link to="/espace/annuaire/participant" className="font-caps text-[0.65rem] px-3 py-2 border" style={{ borderColor: "var(--border)", letterSpacing: "0.18em" }}>PARTICIPANTS</Link>
              <Link to="/espace/annuaire/benevole" className="font-caps text-[0.65rem] px-3 py-2 border" style={{ borderColor: "var(--border)", letterSpacing: "0.18em" }}>BÉNÉVOLES</Link>
              <Link to="/espace/annuaire/organisateur" className="font-caps text-[0.65rem] px-3 py-2 border" style={{ borderColor: "var(--border)", letterSpacing: "0.18em" }}>ORGANISATEURS</Link>
              <Link to="/espace/hebergements" className="font-caps text-[0.65rem] px-3 py-2" style={{ background: "var(--cinnabar)", color: "white", letterSpacing: "0.18em" }}>HÉBERGEMENTS</Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                  <th className="text-left py-2 label-caps">Nom</th>
                  <th className="text-left py-2 label-caps">Email</th>
                  <th className="text-left py-2 label-caps">Rôle</th>
                  <th className="text-left py-2 label-caps">Pays</th>
                  <th className="text-left py-2 label-caps">Téléphone</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-3">{m.name}</td>
                    <td className="py-3">{m.email}</td>
                    <td className="py-3"><span className="font-caps text-[0.65rem]" style={{ letterSpacing: "0.15em" }}>{(m.role || "").toUpperCase()}</span></td>
                    <td className="py-3">{m.country || "—"}</td>
                    <td className="py-3">{m.phone || "—"}</td>
                  </tr>
                ))}
                {members.length === 0 && <tr><td colSpan="5" className="py-6 text-center" style={{ color: "var(--muted)" }}>Aucun membre pour l'instant.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================================== PROFIL
export function EspaceProfil() {
  const { member, updateProfile } = useMember();
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (member) setForm({
    name: member.name || "", phone: member.phone || "", country: member.country || "",
    city: member.city || "", address: member.address || "", bio: member.bio || "",
    organization: member.organization || "", photo_url: member.photo_url || "",
    website: member.website || "", instagram: member.instagram || "",
    youtube: member.youtube || "", facebook: member.facebook || "",
  }); }, [member]);
  if (member === null) return <Shell title="Mon profil"><p className="text-center">Chargement…</p></Shell>;
  if (!member) return <Navigate to="/espace/connexion" replace />;
  if (!form) return null;
  const on = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));
  const isArtiste = member.role === "artiste";

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, photo_url: data.url }));
      setMsg("✓ Photo téléversée. N'oubliez pas d'enregistrer.");
    } catch (ex) { setMsg(formatErr(ex)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try { await updateProfile(form); setMsg("✓ Profil mis à jour."); }
    catch (ex) { setMsg(formatErr(ex)); } finally { setBusy(false); }
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const photoSrc = form.photo_url ? (form.photo_url.startsWith("http") ? form.photo_url : `${BACKEND}${form.photo_url}`) : "";

  return (
    <Shell title="Mon profil">
      <div className="container-prieure max-w-2xl">
        <form onSubmit={submit} className="bg-white border p-8 grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ borderColor: "var(--border)" }}>
          {isArtiste && (
            <div className="sm:col-span-2 flex items-center gap-6 pb-4 mb-2 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="w-24 h-24 border overflow-hidden flex items-center justify-center" style={{ borderColor: "var(--border)", background: "var(--parchment-2)" }}>
                {photoSrc
                  ? <img src={photoSrc} alt="" className="w-full h-full object-cover" />
                  : <span className="font-display text-3xl" style={{ color: "var(--gold)" }}>{(form.name || "?")[0]?.toUpperCase()}</span>}
              </div>
              <div className="flex-1">
                <div className="label-caps mb-1">Photo de profil (affichée publiquement)</div>
                <input
                  type="file" accept="image/*"
                  data-testid="profil-photo-input"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  className="font-body text-sm"
                />
                {uploading && <div className="text-xs italic mt-1">Envoi…</div>}
                <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                  JPG/PNG, 20 Mo max. Conseillé : portrait 4/5, fond uni.
                </div>
              </div>
            </div>
          )}
          <Field label="NOM COMPLET" value={form.name} onChange={on("name")} />
          <Field label="TÉLÉPHONE" value={form.phone} onChange={on("phone")} />
          <Field label="PAYS" value={form.country} onChange={on("country")} />
          <Field label="VILLE" value={form.city} onChange={on("city")} />
          <label className="block sm:col-span-2"><span className="label-caps">ADRESSE</span><input value={form.address} onChange={on("address")} className="input-classic mt-1" /></label>
          <label className="block sm:col-span-2"><span className="label-caps">ASSOCIATION / PAROISSE</span><input value={form.organization} onChange={on("organization")} className="input-classic mt-1" /></label>
          <label className="block sm:col-span-2">
            <span className="label-caps">{isArtiste ? "BIO (AFFICHÉE PUBLIQUEMENT)" : "BIO (COURTE)"}</span>
            <textarea rows={isArtiste ? 5 : 3} value={form.bio} onChange={on("bio")} className="input-classic mt-1" data-testid="profil-bio" />
          </label>
          {isArtiste && (
            <>
              <div className="sm:col-span-2 pt-4 mt-2 border-t" style={{ borderColor: "var(--border)" }}>
                <div className="label-caps">LIENS PUBLICS (OPTIONNELS)</div>
                <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                  Site, portfolio ou réseaux sociaux affichés sur votre fiche artiste publique.
                </p>
              </div>
              <Field label="SITE / PORTFOLIO" value={form.website} onChange={on("website")} data-testid="profil-website" placeholder="https://…" />
              <Field label="INSTAGRAM" value={form.instagram} onChange={on("instagram")} data-testid="profil-instagram" placeholder="https://instagram.com/votreNom" />
              <Field label="YOUTUBE" value={form.youtube} onChange={on("youtube")} data-testid="profil-youtube" placeholder="https://youtube.com/@votreChaine" />
              <Field label="FACEBOOK" value={form.facebook} onChange={on("facebook")} data-testid="profil-facebook" placeholder="https://facebook.com/…" />
            </>
          )}
          {msg && <p className="sm:col-span-2 text-sm" style={{ color: msg.startsWith("✓") ? "var(--moss)" : "var(--cinnabar)" }}>{msg}</p>}
          <div className="sm:col-span-2 flex gap-3 mt-2">
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="profil-save">{busy ? "..." : "Enregistrer"}</button>
            <Link to="/espace" className="btn btn-ghost">Retour au dashboard</Link>
          </div>
        </form>
        <NotificationPrefs />
      </div>
    </Shell>
  );
}


// =========================================================== FORGOT PASSWORD (request reset)
export function EspaceMotDePasseOublie() {
  const { espace, member } = useMember();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (member) return <Navigate to="/espace" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await espace.post("/espace/forgot-password", { email });
      setSent(true);
    } catch (ex) { setErr(formatErr(ex)); }
    finally { setBusy(false); }
  };

  return (
    <Shell title="Mot de passe oublié" compact>
      <div className="container-prieure max-w-sm">
        <div data-testid="forgot-pw-page" className="bg-white border p-6 space-y-3" style={{ borderColor: "var(--border)" }}>
          {sent ? (
            <>
              <p className="font-display text-xl">Vérifiez votre boîte mail</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>
                Si l'adresse <strong>{email}</strong> correspond à un compte actif, un lien de réinitialisation valable
                <strong> 1 heure</strong> vous a été envoyé.
              </p>
              <p className="text-xs italic" style={{ color: "var(--muted)" }}>
                Pensez à vérifier votre dossier spam si vous ne le voyez pas dans quelques minutes.
              </p>
              <Link to="/espace/connexion" className="btn btn-primary w-full block text-center">Retour à la connexion</Link>
            </>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>
                Saisissez l'adresse email associée à votre compte. Nous vous enverrons un lien de réinitialisation.
              </p>
              <Field label="EMAIL" type="email" required value={email}
                     data-testid="forgot-pw-email"
                     onChange={(e) => setEmail(e.target.value)} />
              {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
              <button type="submit" disabled={busy} data-testid="forgot-pw-submit" className="btn btn-primary w-full">
                {busy ? "..." : "Envoyer le lien"}
              </button>
              <Link to="/espace/connexion" className="block text-center link-underline text-sm" style={{ color: "var(--ink-2)" }}>
                Retour à la connexion
              </Link>
            </form>
          )}
        </div>
      </div>
    </Shell>
  );
}

// =========================================================== RESET PASSWORD (with token)
export function EspaceReinitialiserMotDePasse() {
  const [params] = useSearchParams();
  const { espace } = useMember();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); setErr("Lien manquant."); return; }
    espace.get(`/espace/reset-password/${token}`)
      .then((r) => { setInfo(r.data); setStatus("valid"); })
      .catch((ex) => { setErr(formatErr(ex)); setStatus("invalid"); });
  }, [token, espace]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.new_password.length < 8) { setErr("Mot de passe trop court (8 caractères minimum)."); return; }
    if (form.new_password !== form.confirm) { setErr("Les deux mots de passe ne correspondent pas."); return; }
    setBusy(true);
    try {
      await espace.post("/espace/reset-password", { token, new_password: form.new_password });
      setStatus("done");
    } catch (ex) { setErr(formatErr(ex)); }
    finally { setBusy(false); }
  };

  return (
    <Shell title="Réinitialiser mon mot de passe" compact>
      <div className="container-prieure max-w-sm">
        <div data-testid="reset-pw-page" className="bg-white border p-6 space-y-3" style={{ borderColor: "var(--border)" }}>
          {status === "checking" && <p className="text-center" style={{ color: "var(--muted)" }}>Vérification du lien…</p>}

          {status === "invalid" && (
            <>
              <p className="font-display text-xl" style={{ color: "var(--cinnabar)" }}>Lien invalide ou expiré</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>{err}</p>
              <Link to="/espace/mot-de-passe-oublie" className="btn btn-primary w-full block text-center">
                Demander un nouveau lien
              </Link>
            </>
          )}

          {status === "valid" && (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>
                Vous réinitialisez le mot de passe du compte <strong>{info?.email_masked}</strong>.
              </p>
              <label className="block">
                <span className="label-caps">NOUVEAU MOT DE PASSE</span>
                <div className="mt-1">
                  <PasswordInput testId="reset-pw-new" required minLength={8} value={form.new_password}
                                 onChange={(e) => setForm((s) => ({ ...s, new_password: e.target.value }))}
                                 autoComplete="new-password" />
                </div>
              </label>
              <label className="block">
                <span className="label-caps">CONFIRMER</span>
                <div className="mt-1">
                  <PasswordInput testId="reset-pw-confirm" required minLength={8} value={form.confirm}
                                 onChange={(e) => setForm((s) => ({ ...s, confirm: e.target.value }))}
                                 autoComplete="new-password" />
                </div>
              </label>
              {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
              <button type="submit" disabled={busy} data-testid="reset-pw-submit" className="btn btn-primary w-full">
                {busy ? "..." : "Choisir ce mot de passe"}
              </button>
            </form>
          )}

          {status === "done" && (
            <>
              <p className="font-display text-xl" style={{ color: "var(--moss)" }}>✓ Mot de passe modifié</p>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>
                Votre mot de passe a été changé avec succès. Vous pouvez maintenant vous connecter.
              </p>
              <Link to="/espace/connexion" className="btn btn-primary w-full block text-center">
                Se connecter
              </Link>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}
